import { Tooltip } from "../common/Tooltip";

interface MetaMoreProps {
  /** Every value in the list, the visible one included. */
  items: string[];
  /** Heading for the bubble, e.g. "Second parties". */
  title?: string;
  /** How many are already shown beside the pill (default 1). */
  shown?: number;
  /** List the values in the tooltip as chips rather than a numbered list —
   *  for values that are chips in the tile too (a member's roles). */
  pills?: boolean;
}

/**
 * A "+N" pill for a meta value that is really a list — several second
 * parties on one contract, say — where the tile has room for only the first.
 * Hovering, focusing or clicking the pill lists every value in a tooltip.
 */
export function MetaMore({
  items,
  title,
  shown = 1,
  pills = false,
}: MetaMoreProps) {
  const hidden = items.length - shown;
  if (hidden <= 0) return null;

  return (
    <Tooltip
      content={
        <span className="block text-left">
          {title && (
            <span className="block mb-1 text-[11px] uppercase tracking-wide text-[rgb(var(--text-muted))]">
              {title}
            </span>
          )}
          {pills ? (
            <span className="flex max-w-[16rem] flex-wrap gap-1">
              {items.map((item, i) => (
                <span
                  key={`${item}-${i}`}
                  className="inline-flex items-center rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
                >
                  {item}
                </span>
              ))}
            </span>
          ) : (
            items.map((item, i) => (
              <span key={`${item}-${i}`} className="block whitespace-nowrap">
                {i + 1}. {item}
              </span>
            ))
          )}
        </span>
      }
    >
      <button
        type="button"
        // Safari does not focus a button on click, and the tooltip opens on
        // focus, so focus it by hand to make a click show the list too.
        onClick={(e) => {
          e.stopPropagation();
          e.currentTarget.focus();
        }}
        aria-label={title ? `${title}: ${items.join(", ")}` : items.join(", ")}
        className="
          inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5
          text-[11px] font-semibold leading-none
          bg-[rgb(var(--text-accent))]/10 text-[rgb(var(--text-accent))]
          hover:bg-[rgb(var(--text-accent))]/20
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]
        "
      >
        +{hidden}
      </button>
    </Tooltip>
  );
}
